/**
 * Finance Admin Dashboard - Enhanced
 * Admin controls for finance module - providers, categories, monetization, notifications
 * Includes: Calculator settings per provider, commission tracking, email notifications
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Separator } from '../../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../../../components/ui/accordion';
import { useToast } from '../../../hooks/use-toast';
import {
  Banknote,
  Building2,
  Users,
  Mail,
  Plus,
  Edit,
  Shield,
  CheckCircle2,
  XCircle,
  Clock,
  Star,
  TrendingUp,
  ArrowLeft,
  Loader2,
  Settings,
  BarChart3,
  Send,
  Ban,
  BadgeCheck,
  AlertTriangle,
  DollarSign,
  Calculator,
  Bell,
  CreditCard,
  Percent,
  Receipt,
  FileText,
  PiggyBank,
  Eye,
  RefreshCw,
  Car,
  Info
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinanceAdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('providers');
  const [adminId] = useState('admin_user');
  
  // Data states
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState({});
  
  // Dialog states
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerDialogTab, setProviderDialogTab] = useState('basic');
  
  // Provider form with calculator & monetization settings
  const [providerForm, setProviderForm] = useState({
    // Basic Info
    name: '',
    description: '',
    website_url: '',
    countries: ['SL'],
    provider_type: 'finance_company',
    is_regulated: true,
    regulation_number: '',
    
    // Loan Terms
    accepts_new_vehicles: true,
    accepts_used_vehicles: true,
    max_vehicle_age: 15,
    min_loan_amount: 500000,
    max_loan_amount: 20000000,
    min_tenure_months: 12,
    max_tenure_months: 84,
    interest_rate_min: 8,
    interest_rate_max: 15,
    interest_level: 'medium',
    min_deposit_percent: 10,
    deposit_required: 'low',
    approval_time: '1-3 days',
    online_application: true,
    documents_required: 'standard',
    income_types_accepted: ['salaried'],
    early_repayment_penalty: false,
    high_approval_likelihood: false,
    
    // Calculator Settings (NEW)
    show_calculator: true,
    calculator_enabled: true,
    representative_apr: 9.9,
    rate_excellent: 8.5,
    rate_good: 10.9,
    rate_fair: 13.5,
    rate_poor: 18.0,
    tenure_options: [12, 24, 36, 48, 60, 72, 84],
    
    // Monetization (NEW)
    commission_type: 'per_lead',
    commission_rate: 0,
    lead_price: 50,
    approval_bonus: 100,
    disbursement_commission_percent: 0.5,
    is_sponsored: false,
    sponsored_position: null,
    monthly_lead_cap: null
  });
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    company_name: '',
    country: 'SL',
    message: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [providersRes, categoriesRes, invitationsRes, referralsRes, analyticsRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/finance/admin/providers`),
        axios.get(`${API_URL}/api/finance/admin/categories`),
        axios.get(`${API_URL}/api/finance/admin/invitations`),
        axios.get(`${API_URL}/api/finance/admin/referrals?limit=50`),
        axios.get(`${API_URL}/api/finance/admin/analytics`),
        axios.get(`${API_URL}/api/finance/admin/config`)
      ]);
      
      if (providersRes.data.success) setProviders(providersRes.data.providers);
      if (categoriesRes.data.success) setCategories(categoriesRes.data.categories);
      if (invitationsRes.data.success) setInvitations(invitationsRes.data.invitations);
      if (referralsRes.data.success) setReferrals(referralsRes.data.referrals);
      if (analyticsRes.data.success) setAnalytics(analyticsRes.data.analytics);
      if (configRes.data.success) setConfig(configRes.data.config);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: "Error", description: "Failed to load finance data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.post(`${API_URL}/api/finance/admin/config?admin_id=${adminId}`, config);
      toast({ title: "Success", description: "Configuration saved" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save config", variant: "destructive" });
    }
  };

  const handleSaveProvider = async () => {
    try {
      if (editingProvider) {
        await axios.put(
          `${API_URL}/api/finance/admin/providers/${editingProvider.id}?admin_id=${adminId}`,
          providerForm
        );
        toast({ title: "Success", description: "Provider updated" });
      } else {
        await axios.post(`${API_URL}/api/finance/admin/providers?admin_id=${adminId}`, providerForm);
        toast({ title: "Success", description: "Provider created" });
      }
      
      setShowProviderDialog(false);
      setEditingProvider(null);
      resetProviderForm();
      fetchData();
      
    } catch (error) {
      toast({ title: "Error", description: "Failed to save provider", variant: "destructive" });
    }
  };

  const handleToggleProviderStatus = async (provider, field, value) => {
    try {
      await axios.patch(
        `${API_URL}/api/finance/admin/providers/${provider.id}/status?admin_id=${adminId}`,
        { [field]: value }
      );
      fetchData();
      toast({ title: "Success", description: `Provider ${field} updated` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleDeleteProvider = async (provider) => {
    if (!window.confirm(`Are you sure you want to deactivate ${provider.name}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/finance/admin/providers/${provider.id}?admin_id=${adminId}`);
      fetchData();
      toast({ title: "Success", description: "Provider deactivated" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete provider", variant: "destructive" });
    }
  };

  const handleSendInvitation = async () => {
    try {
      await axios.post(`${API_URL}/api/finance/admin/invitations?admin_id=${adminId}`, inviteForm);
      toast({ title: "Success", description: `Invitation sent to ${inviteForm.email}` });
      setShowInviteDialog(false);
      setInviteForm({ email: '', company_name: '', country: 'SL', message: '' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send invitation", variant: "destructive" });
    }
  };

  const handleToggleCategory = async (category) => {
    try {
      await axios.patch(
        `${API_URL}/api/finance/admin/categories/${category.id}?admin_id=${adminId}`,
        { enabled: !category.enabled }
      );
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to toggle category", variant: "destructive" });
    }
  };

  const openEditProvider = (provider) => {
    setEditingProvider(provider);
    setProviderForm({
      name: provider.name || '',
      description: provider.description || '',
      website_url: provider.website_url || '',
      countries: provider.countries || ['SL'],
      provider_type: provider.provider_type || 'finance_company',
      is_regulated: provider.is_regulated ?? true,
      regulation_number: provider.regulation_number || '',
      accepts_new_vehicles: provider.accepts_new_vehicles ?? true,
      accepts_used_vehicles: provider.accepts_used_vehicles ?? true,
      max_vehicle_age: provider.max_vehicle_age || 15,
      min_loan_amount: provider.min_loan_amount || 500000,
      max_loan_amount: provider.max_loan_amount || 20000000,
      min_tenure_months: provider.min_tenure_months || 12,
      max_tenure_months: provider.max_tenure_months || 84,
      interest_rate_min: provider.interest_rate_min || 8,
      interest_rate_max: provider.interest_rate_max || 15,
      interest_level: provider.interest_level || 'medium',
      min_deposit_percent: provider.min_deposit_percent || 10,
      deposit_required: provider.deposit_required || 'low',
      approval_time: provider.approval_time || '1-3 days',
      online_application: provider.online_application ?? true,
      documents_required: provider.documents_required || 'standard',
      income_types_accepted: provider.income_types_accepted || ['salaried'],
      early_repayment_penalty: provider.early_repayment_penalty ?? false,
      high_approval_likelihood: provider.high_approval_likelihood ?? false,
      // Calculator
      show_calculator: provider.show_calculator ?? true,
      calculator_enabled: provider.calculator_enabled ?? true,
      representative_apr: provider.representative_apr || 9.9,
      rate_excellent: provider.rate_excellent || 8.5,
      rate_good: provider.rate_good || 10.9,
      rate_fair: provider.rate_fair || 13.5,
      rate_poor: provider.rate_poor || 18.0,
      tenure_options: provider.tenure_options || [12, 24, 36, 48, 60, 72, 84],
      // Monetization
      commission_type: provider.commission_type || 'per_lead',
      commission_rate: provider.commission_rate || 0,
      lead_price: provider.lead_price || 50,
      approval_bonus: provider.approval_bonus || 100,
      disbursement_commission_percent: provider.disbursement_commission_percent || 0.5,
      is_sponsored: provider.is_sponsored ?? false,
      sponsored_position: provider.sponsored_position || null,
      monthly_lead_cap: provider.monthly_lead_cap || null
    });
    setProviderDialogTab('basic');
    setShowProviderDialog(true);
  };

  const resetProviderForm = () => {
    setProviderForm({
      name: '',
      description: '',
      website_url: '',
      countries: ['SL'],
      provider_type: 'finance_company',
      is_regulated: true,
      regulation_number: '',
      accepts_new_vehicles: true,
      accepts_used_vehicles: true,
      max_vehicle_age: 15,
      min_loan_amount: 500000,
      max_loan_amount: 20000000,
      min_tenure_months: 12,
      max_tenure_months: 84,
      interest_rate_min: 8,
      interest_rate_max: 15,
      interest_level: 'medium',
      min_deposit_percent: 10,
      deposit_required: 'low',
      approval_time: '1-3 days',
      online_application: true,
      documents_required: 'standard',
      income_types_accepted: ['salaried'],
      early_repayment_penalty: false,
      high_approval_likelihood: false,
      show_calculator: true,
      calculator_enabled: true,
      representative_apr: 9.9,
      rate_excellent: 8.5,
      rate_good: 10.9,
      rate_fair: 13.5,
      rate_poor: 18.0,
      tenure_options: [12, 24, 36, 48, 60, 72, 84],
      commission_type: 'per_lead',
      commission_rate: 0,
      lead_price: 50,
      approval_bonus: 100,
      disbursement_commission_percent: 0.5,
      is_sponsored: false,
      sponsored_position: null,
      monthly_lead_cap: null
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': case 'disbursed': case 'paid': return 'bg-green-100 text-green-800';
      case 'in_review': case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate monetization summary
  const monetizationSummary = {
    totalLeads: referrals.length,
    pendingCommission: referrals.filter(r => r.commission_status === 'pending').length,
    paidCommission: referrals.filter(r => r.commission_status === 'paid').length,
    sponsoredProviders: providers.filter(p => p.is_sponsored).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="finance-admin-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/vehicles')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Banknote className="h-6 w-6 text-green-600" />
                Finance Module Admin
              </h1>
              <p className="text-gray-500">Manage providers, calculator settings, and monetization</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowInviteDialog(true)}>
              <Mail className="h-4 w-4 mr-2" />
              Invite Lender
            </Button>
            <Button onClick={() => {
              setEditingProvider(null);
              resetProviderForm();
              setProviderDialogTab('basic');
              setShowProviderDialog(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Providers</p>
                  <p className="text-2xl font-bold">{providers.filter(p => p.is_active).length}</p>
                </div>
                <Building2 className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold">{analytics?.total_applications || 0}</p>
                </div>
                <Users className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{analytics?.approved || 0}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Conversion</p>
                  <p className="text-2xl font-bold">{analytics?.conversion_rate || 0}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Revenue (Pending)</p>
                  <p className="text-2xl font-bold text-green-700">{monetizationSummary.pendingCommission} leads</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="providers" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Providers
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="monetization" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Monetization
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle>Finance Providers</CardTitle>
                <CardDescription>Manage lenders, calculator settings, and commission rates</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Calculator</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map(provider => (
                      <TableRow key={provider.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{provider.name}</div>
                            {provider.is_verified && <BadgeCheck className="h-4 w-4 text-blue-500" />}
                            {provider.is_sponsored && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                          </div>
                          <div className="text-xs text-gray-500">{provider.countries?.join(', ')}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {provider.provider_type?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {provider.show_calculator !== false ? (
                              <Badge className="bg-green-100 text-green-800">
                                <Calculator className="h-3 w-3 mr-1" />
                                Enabled
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                <XCircle className="h-3 w-3 mr-1" />
                                Disabled
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">{provider.commission_type?.replace('_', ' ')}</div>
                            <div className="text-gray-500">
                              {provider.lead_price ? `$${provider.lead_price}/lead` : 'Not set'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={provider.is_active}
                              onCheckedChange={(v) => handleToggleProviderStatus(provider, 'is_active', v)}
                            />
                            <span className={provider.is_active ? 'text-green-600' : 'text-gray-400'}>
                              {provider.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditProvider(provider)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleProviderStatus(provider, 'is_sponsored', !provider.is_sponsored)}
                              title={provider.is_sponsored ? 'Remove Sponsor' : 'Mark Sponsored'}
                            >
                              <Star className={`h-4 w-4 ${provider.is_sponsored ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => handleDeleteProvider(provider)}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {providers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          No providers yet. Add your first finance provider.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Finance Categories</CardTitle>
                <CardDescription>Control which badges/categories are shown to buyers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categories.map(category => (
                    <Card key={category.id} className={!category.enabled ? 'opacity-50' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{category.icon}</span>
                            <div>
                              <div className="font-medium">{category.name}</div>
                              <div className="text-sm text-gray-500">{category.description}</div>
                            </div>
                          </div>
                          <Switch
                            checked={category.enabled}
                            onCheckedChange={() => handleToggleCategory(category)}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monetization Tab */}
          <TabsContent value="monetization">
            <div className="space-y-6">
              {/* Monetization Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <PiggyBank className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-blue-700">Cost-per-Lead Revenue</p>
                        <p className="text-2xl font-bold text-blue-800">
                          ${referrals.length * (config.default_lead_price || 50)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm text-green-700">Approval Bonuses</p>
                        <p className="text-2xl font-bold text-green-800">
                          ${(analytics?.approved || 0) * (config.default_approval_bonus || 100)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-r from-purple-50 to-violet-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Star className="h-8 w-8 text-purple-600" />
                      <div>
                        <p className="text-sm text-purple-700">Sponsored Slots</p>
                        <p className="text-2xl font-bold text-purple-800">
                          {monetizationSummary.sponsoredProviders} / {config.sponsored_placements_max || 2}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-r from-orange-50 to-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Receipt className="h-8 w-8 text-orange-600" />
                      <div>
                        <p className="text-sm text-orange-700">Pending Payments</p>
                        <p className="text-2xl font-bold text-orange-800">
                          {monetizationSummary.pendingCommission}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cost-per-Lead Examples by Vehicle Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Cost-per-Lead Pricing Guide
                  </CardTitle>
                  <CardDescription>
                    Revenue structure based on vehicle price categories. Both default and provider-configurable rates.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Budget Cars */}
                    <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <Car className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">Budget Cars</h4>
                          <p className="text-xs text-gray-500">Rs. 500K - 2M / £5K - 15K</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lead Fee:</span>
                          <span className="font-semibold text-blue-600">$30</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Approval Bonus:</span>
                          <span className="font-semibold text-green-600">$50</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Disbursement:</span>
                          <span className="font-semibold text-purple-600">0.3%</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="text-xs text-gray-500">
                          <strong>Example:</strong> Rs. 1.5M car = $30 + $50 + $4,500 = <span className="text-green-600 font-bold">$4,580</span>
                        </div>
                      </div>
                    </div>

                    {/* Mid-Range Cars */}
                    <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                          <Car className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-blue-800">Mid-Range Cars</h4>
                          <p className="text-xs text-blue-500">Rs. 2M - 7M / £15K - 35K</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lead Fee:</span>
                          <span className="font-semibold text-blue-600">$50</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Approval Bonus:</span>
                          <span className="font-semibold text-green-600">$100</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Disbursement:</span>
                          <span className="font-semibold text-purple-600">0.5%</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="text-xs text-gray-500">
                          <strong>Example:</strong> Rs. 5M car = $50 + $100 + $25,000 = <span className="text-green-600 font-bold">$25,150</span>
                        </div>
                      </div>
                    </div>

                    {/* Premium Cars */}
                    <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
                          <Car className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-purple-800">Premium Cars</h4>
                          <p className="text-xs text-purple-500">Rs. 7M - 20M / £35K - 80K</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lead Fee:</span>
                          <span className="font-semibold text-blue-600">$75</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Approval Bonus:</span>
                          <span className="font-semibold text-green-600">$150</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Disbursement:</span>
                          <span className="font-semibold text-purple-600">0.5%</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="text-xs text-gray-500">
                          <strong>Example:</strong> Rs. 15M car = $75 + $150 + $75,000 = <span className="text-green-600 font-bold">$75,225</span>
                        </div>
                      </div>
                    </div>

                    {/* Luxury Cars */}
                    <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
                          <Star className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-amber-800">Luxury Cars</h4>
                          <p className="text-xs text-amber-500">Rs. 20M+ / £80K+</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lead Fee:</span>
                          <span className="font-semibold text-blue-600">$100</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Approval Bonus:</span>
                          <span className="font-semibold text-green-600">$200</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Disbursement:</span>
                          <span className="font-semibold text-purple-600">0.4%</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="text-xs text-gray-500">
                          <strong>Example:</strong> Rs. 30M car = $100 + $200 + $120,000 = <span className="text-green-600 font-bold">$120,300</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Alert className="mt-4 bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-sm text-blue-800">
                      <strong>Revenue Model:</strong> Lead Fee (charged on application) + Approval Bonus (on approval) + Disbursement Commission (% of loan when disbursed). 
                      Providers can override default rates with their own pricing in their profile settings.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Sponsored Providers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    Sponsored Placements
                  </CardTitle>
                  <CardDescription>
                    Sponsored providers appear at the top of search results (max {config.sponsored_placements_max || 2} positions)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Position</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Countries</TableHead>
                        <TableHead>Monthly Fee</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providers.filter(p => p.is_sponsored).map((provider, idx) => (
                        <TableRow key={provider.id}>
                          <TableCell>
                            <Badge className="bg-yellow-100 text-yellow-800">#{idx + 1}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{provider.name}</TableCell>
                          <TableCell>{provider.countries?.join(', ')}</TableCell>
                          <TableCell>$500/mo</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleProviderStatus(provider, 'is_sponsored', false)}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {providers.filter(p => p.is_sponsored).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                            No sponsored providers. Click the star icon on a provider to add.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Default Commission Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Default Commission Rates</CardTitle>
                  <CardDescription>Set default rates for new providers (can be overridden per provider)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Default Lead Fee ($)</Label>
                      <Input
                        type="number"
                        value={config.default_lead_price || 50}
                        onChange={(e) => setConfig(prev => ({ ...prev, default_lead_price: parseFloat(e.target.value) }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">Charged when buyer submits application</p>
                    </div>
                    <div>
                      <Label>Default Approval Bonus ($)</Label>
                      <Input
                        type="number"
                        value={config.default_approval_bonus || 100}
                        onChange={(e) => setConfig(prev => ({ ...prev, default_approval_bonus: parseFloat(e.target.value) }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">Charged when application is approved</p>
                    </div>
                    <div>
                      <Label>Default Disbursement (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={config.default_disbursement_percent || 0.5}
                        onChange={(e) => setConfig(prev => ({ ...prev, default_disbursement_percent: parseFloat(e.target.value) }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">% of loan amount on disbursement</p>
                    </div>
                  </div>
                  <Button onClick={handleSaveConfig}>Save Commission Settings</Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Finance Referrals & Commissions</CardTitle>
                <CardDescription>Track all finance applications and commission status</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Lead Status</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map(referral => (
                      <TableRow key={referral.id}>
                        <TableCell className="text-gray-500">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="font-medium">{referral.provider_id}</TableCell>
                        <TableCell><Badge variant="secondary">{referral.country}</Badge></TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(referral.status)}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(referral.commission_status)}>
                            {referral.commission_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">$50</TableCell>
                      </TableRow>
                    ))}
                    {referrals.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          No referrals yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Email Notification Settings
                </CardTitle>
                <CardDescription>Configure automatic email alerts for finance events</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert className="bg-blue-50 border-blue-200">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Configure when to send email notifications to buyers and lenders for finance application updates.
                  </AlertDescription>
                </Alert>
                
                {/* Buyer Notifications */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Buyer Notifications
                  </h4>
                  <div className="space-y-3 pl-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Application Submitted</div>
                        <div className="text-sm text-gray-500">Notify buyer when application is received</div>
                      </div>
                      <Switch
                        checked={config.notify_buyer_on_submit ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_submit: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Application In Review</div>
                        <div className="text-sm text-gray-500">Notify buyer when lender starts review</div>
                      </div>
                      <Switch
                        checked={config.notify_buyer_on_review ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_review: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div>
                        <div className="font-medium text-green-800">Application Approved</div>
                        <div className="text-sm text-green-600">Notify buyer when application is approved</div>
                      </div>
                      <Switch
                        checked={config.notify_buyer_on_approval ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_approval: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div>
                        <div className="font-medium text-red-800">Application Rejected</div>
                        <div className="text-sm text-red-600">Notify buyer when application is rejected</div>
                      </div>
                      <Switch
                        checked={config.notify_buyer_on_rejection ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_rejection: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div>
                        <div className="font-medium text-yellow-800">Documents Requested</div>
                        <div className="text-sm text-yellow-600">Notify buyer when lender requests documents</div>
                      </div>
                      <Switch
                        checked={config.notify_buyer_on_docs_request ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_docs_request: v }))}
                      />
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Lender Notifications */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Lender Notifications
                  </h4>
                  <div className="space-y-3 pl-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">New Lead Received</div>
                        <div className="text-sm text-gray-500">Notify lender when new application is received</div>
                      </div>
                      <Switch
                        checked={config.notify_lender_on_new_lead ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_lender_on_new_lead: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Daily Lead Summary</div>
                        <div className="text-sm text-gray-500">Send daily summary of new leads</div>
                      </div>
                      <Switch
                        checked={config.notify_lender_daily_summary ?? false}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_lender_daily_summary: v }))}
                      />
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Admin Notifications */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Admin Notifications
                  </h4>
                  <div className="space-y-3 pl-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">New Lender Registration</div>
                        <div className="text-sm text-gray-500">Alert when new lender registers for approval</div>
                      </div>
                      <Switch
                        checked={config.notify_admin_new_lender ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_admin_new_lender: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">High Volume Alert</div>
                        <div className="text-sm text-gray-500">Alert when leads exceed threshold</div>
                      </div>
                      <Switch
                        checked={config.notify_admin_high_volume ?? false}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_admin_high_volume: v }))}
                      />
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSaveConfig} className="mt-4">
                  Save Notification Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Module Settings</CardTitle>
                <CardDescription>Configure finance module behavior</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold">General Settings</h4>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Finance Module Enabled</div>
                        <div className="text-sm text-gray-500">Show finance options on listings</div>
                      </div>
                      <Switch
                        checked={config.module_enabled ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, module_enabled: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Categories/Badges Enabled</div>
                        <div className="text-sm text-gray-500">Show quick filter badges</div>
                      </div>
                      <Switch
                        checked={config.categories_enabled ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, categories_enabled: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Eligibility Matching</div>
                        <div className="text-sm text-gray-500">Calculate eligibility scores</div>
                      </div>
                      <Switch
                        checked={config.eligibility_matching_enabled ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, eligibility_matching_enabled: v }))}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-semibold">Portal Settings</h4>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Lender Portal Enabled</div>
                        <div className="text-sm text-gray-500">Allow lender self-registration</div>
                      </div>
                      <Switch
                        checked={config.lender_portal_enabled ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, lender_portal_enabled: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Referral Tracking</div>
                        <div className="text-sm text-gray-500">Track applications for commissions</div>
                      </div>
                      <Switch
                        checked={config.referral_tracking_enabled ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, referral_tracking_enabled: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Sponsored Placements</div>
                        <div className="text-sm text-gray-500">Allow sponsored providers</div>
                      </div>
                      <Switch
                        checked={config.sponsored_placements_enabled ?? true}
                        onCheckedChange={(v) => setConfig(prev => ({ ...prev, sponsored_placements_enabled: v }))}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label>Max Sponsored Positions</Label>
                  <Input
                    type="number"
                    value={config.sponsored_placements_max || 2}
                    onChange={(e) => setConfig(prev => ({ ...prev, sponsored_placements_max: parseInt(e.target.value) }))}
                    className="w-32 mt-1"
                    min={0}
                    max={5}
                  />
                </div>
                
                <Button onClick={handleSaveConfig}>Save Settings</Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Provider Dialog - Enhanced with Calculator & Monetization */}
      <Dialog open={showProviderDialog} onOpenChange={setShowProviderDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProvider ? 'Edit Provider' : 'Add Finance Provider'}</DialogTitle>
            <DialogDescription>Configure provider details, calculator settings, and commission rates</DialogDescription>
          </DialogHeader>
          
          <Tabs value={providerDialogTab} onValueChange={setProviderDialogTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="terms">Loan Terms</TabsTrigger>
              <TabsTrigger value="calculator">Calculator</TabsTrigger>
              <TabsTrigger value="monetization">Monetization</TabsTrigger>
            </TabsList>
            
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Provider Name *</Label>
                  <Input
                    value={providerForm.name}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., National Auto Finance"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={providerForm.description}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Website URL</Label>
                  <Input
                    value={providerForm.website_url}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, website_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Provider Type</Label>
                  <Select
                    value={providerForm.provider_type}
                    onValueChange={(v) => setProviderForm(prev => ({ ...prev, provider_type: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="finance_company">Finance Company</SelectItem>
                      <SelectItem value="dealer_finance">Dealer Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Countries</Label>
                <div className="flex gap-2 mt-1">
                  {['SL', 'UK', 'US', 'AU', 'CA', 'IE', 'NZ'].map(code => (
                    <Button
                      key={code}
                      type="button"
                      variant={providerForm.countries.includes(code) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setProviderForm(prev => ({
                          ...prev,
                          countries: prev.countries.includes(code)
                            ? prev.countries.filter(c => c !== code)
                            : [...prev.countries, code]
                        }));
                      }}
                    >
                      {code}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={providerForm.is_regulated}
                    onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, is_regulated: v }))}
                  />
                  <Label>Regulated Lender</Label>
                </div>
                <div className="flex-1">
                  <Input
                    value={providerForm.regulation_number}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, regulation_number: e.target.value }))}
                    placeholder="Licence/Registration number"
                  />
                </div>
              </div>
            </TabsContent>
            
            {/* Loan Terms Tab */}
            <TabsContent value="terms" className="space-y-4 mt-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Min Loan</Label>
                  <Input
                    type="number"
                    value={providerForm.min_loan_amount}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, min_loan_amount: parseFloat(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Max Loan</Label>
                  <Input
                    type="number"
                    value={providerForm.max_loan_amount}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, max_loan_amount: parseFloat(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Min Interest (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={providerForm.interest_rate_min}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, interest_rate_min: parseFloat(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Max Interest (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={providerForm.interest_rate_max}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, interest_rate_max: parseFloat(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Min Deposit (%)</Label>
                  <Input
                    type="number"
                    value={providerForm.min_deposit_percent}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, min_deposit_percent: parseFloat(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Approval Time</Label>
                  <Select
                    value={providerForm.approval_time}
                    onValueChange={(v) => setProviderForm(prev => ({ ...prev, approval_time: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">Instant</SelectItem>
                      <SelectItem value="<24h">Less than 24h</SelectItem>
                      <SelectItem value="1-3 days">1-3 Days</SelectItem>
                      <SelectItem value=">3 days">More than 3 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Min Tenure</Label>
                  <Input
                    type="number"
                    value={providerForm.min_tenure_months}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, min_tenure_months: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Max Tenure</Label>
                  <Input
                    type="number"
                    value={providerForm.max_tenure_months}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, max_tenure_months: parseInt(e.target.value) }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={providerForm.accepts_new_vehicles}
                    onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, accepts_new_vehicles: v }))}
                  />
                  <Label>Accepts New Vehicles</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={providerForm.accepts_used_vehicles}
                    onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, accepts_used_vehicles: v }))}
                  />
                  <Label>Accepts Used Vehicles</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={providerForm.online_application}
                    onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, online_application: v }))}
                  />
                  <Label>Online Application</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={providerForm.early_repayment_penalty}
                    onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, early_repayment_penalty: v }))}
                  />
                  <Label>Early Repayment Penalty</Label>
                </div>
              </div>
              
              <div>
                <Label>Income Types Accepted</Label>
                <div className="flex gap-2 mt-1">
                  {['salaried', 'self_employed', 'business'].map(type => (
                    <Button
                      key={type}
                      type="button"
                      variant={providerForm.income_types_accepted.includes(type) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setProviderForm(prev => ({
                          ...prev,
                          income_types_accepted: prev.income_types_accepted.includes(type)
                            ? prev.income_types_accepted.filter(t => t !== type)
                            : [...prev.income_types_accepted, type]
                        }));
                      }}
                    >
                      {type.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            </TabsContent>
            
            {/* Calculator Settings Tab */}
            <TabsContent value="calculator" className="space-y-4 mt-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Calculator className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Configure the finance calculator shown to buyers. Disable to send quotes after application.
                </AlertDescription>
              </Alert>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium">Show Calculator to Buyers</div>
                  <div className="text-sm text-gray-500">Display detailed breakdown before application</div>
                </div>
                <Switch
                  checked={providerForm.show_calculator}
                  onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, show_calculator: v }))}
                />
              </div>
              
              {providerForm.show_calculator && (
                <>
                  <div>
                    <Label>Representative APR (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={providerForm.representative_apr}
                      onChange={(e) => setProviderForm(prev => ({ ...prev, representative_apr: parseFloat(e.target.value) }))}
                      className="w-32"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used in representative example text</p>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Credit Score Rate Tiers
                    </h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label className="text-green-700">Excellent (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={providerForm.rate_excellent}
                          onChange={(e) => setProviderForm(prev => ({ ...prev, rate_excellent: parseFloat(e.target.value) }))}
                        />
                      </div>
                      <div>
                        <Label className="text-blue-700">Good (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={providerForm.rate_good}
                          onChange={(e) => setProviderForm(prev => ({ ...prev, rate_good: parseFloat(e.target.value) }))}
                        />
                      </div>
                      <div>
                        <Label className="text-yellow-700">Fair (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={providerForm.rate_fair}
                          onChange={(e) => setProviderForm(prev => ({ ...prev, rate_fair: parseFloat(e.target.value) }))}
                        />
                      </div>
                      <div>
                        <Label className="text-red-700">Poor (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={providerForm.rate_poor}
                          onChange={(e) => setProviderForm(prev => ({ ...prev, rate_poor: parseFloat(e.target.value) }))}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Available Tenure Options (months)</Label>
                    <div className="flex gap-2 mt-1">
                      {[12, 24, 36, 48, 60, 72, 84].map(months => (
                        <Button
                          key={months}
                          type="button"
                          variant={providerForm.tenure_options.includes(months) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setProviderForm(prev => ({
                              ...prev,
                              tenure_options: prev.tenure_options.includes(months)
                                ? prev.tenure_options.filter(m => m !== months)
                                : [...prev.tenure_options, months].sort((a, b) => a - b)
                            }));
                          }}
                        >
                          {months}
                        </Button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
            
            {/* Monetization Tab */}
            <TabsContent value="monetization" className="space-y-4 mt-4">
              <Alert className="bg-green-50 border-green-200">
                <DollarSign className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Configure commission rates and monetization settings for this provider.
                </AlertDescription>
              </Alert>
              
              <div>
                <Label>Commission Type</Label>
                <Select
                  value={providerForm.commission_type}
                  onValueChange={(v) => setProviderForm(prev => ({ ...prev, commission_type: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_lead">Per Lead (Recommended)</SelectItem>
                    <SelectItem value="per_approval">Per Approval</SelectItem>
                    <SelectItem value="per_disbursement">Per Disbursement</SelectItem>
                    <SelectItem value="hybrid">Hybrid (Lead + Bonus)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Lead Price ($)</Label>
                  <Input
                    type="number"
                    value={providerForm.lead_price}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, lead_price: parseFloat(e.target.value) }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Paid per qualified lead</p>
                </div>
                <div>
                  <Label>Approval Bonus ($)</Label>
                  <Input
                    type="number"
                    value={providerForm.approval_bonus}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, approval_bonus: parseFloat(e.target.value) }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Extra when lead is approved</p>
                </div>
                <div>
                  <Label>Disbursement (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={providerForm.disbursement_commission_percent}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, disbursement_commission_percent: parseFloat(e.target.value) }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">% of loan amount</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                <div>
                  <div className="font-medium text-yellow-800 flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Sponsored Placement
                  </div>
                  <div className="text-sm text-yellow-600">Show at top of search results</div>
                </div>
                <Switch
                  checked={providerForm.is_sponsored}
                  onCheckedChange={(v) => setProviderForm(prev => ({ ...prev, is_sponsored: v }))}
                />
              </div>
              
              <div>
                <Label>Monthly Lead Cap</Label>
                <Input
                  type="number"
                  value={providerForm.monthly_lead_cap || ''}
                  onChange={(e) => setProviderForm(prev => ({ ...prev, monthly_lead_cap: e.target.value ? parseInt(e.target.value) : null }))}
                  placeholder="No limit"
                  className="w-32"
                />
                <p className="text-xs text-gray-500 mt-1">Stop showing provider after cap is reached</p>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowProviderDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProvider}>
              {editingProvider ? 'Update Provider' : 'Create Provider'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Finance Provider</DialogTitle>
            <DialogDescription>Send an invitation to a lender to join the platform</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Company Name *</Label>
              <Input
                value={inviteForm.company_name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="e.g., ABC Finance Ltd"
              />
            </div>
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Select
                value={inviteForm.country}
                onValueChange={(v) => setInviteForm(prev => ({ ...prev, country: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SL">Sri Lanka</SelectItem>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Personal Message</Label>
              <Textarea
                value={inviteForm.message}
                onChange={(e) => setInviteForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Add a personal message..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleSendInvitation}>
              <Send className="h-4 w-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceAdminDashboard;
